import Sentry from "@similarweb/sentry-logger";
import { MixpanelTracker as MixpanelTrackerInternal } from "@similarweb/sw-track";
import type { CustomAttribute, ICustomDimensionData, Primitive, TrackingEvent, TrackModel } from "@similarweb/sw-track";

import { propertiesToResetOnPageView } from "app/services/track/trackers/propertiesToResetOnPageView";

type QueuedCallback = (...args: unknown[]) => unknown;

interface ITrackEventsQueue {
    args: Array<unknown>;
    callback: QueuedCallback;
}

let isReadyAndIdentified = false;
const trackEventsQueue: Array<ITrackEventsQueue> = [];

const onReadyAndIdentified = () => {
    isReadyAndIdentified = true;
    while (trackEventsQueue.length > 0) {
        const { callback, args } = trackEventsQueue.shift()!;
        callback(...args);
    }
};

const onMixpanelErrorOnLoadingScript = (e: Event) => {
    Sentry.error("Error while loading mixpanel script", e);
};

window.addEventListener("MixpanelReadyAndIdentified", onReadyAndIdentified);
document.addEventListener("MixpanelErrorOnLoadingScript", onMixpanelErrorOnLoadingScript);

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
export abstract class MixpanelTracker extends MixpanelTrackerInternal {
    private withTrackEventsQueue<A extends unknown[]>(callback: (...args: A) => unknown, ...args: A): void {
        if (isReadyAndIdentified) {
            callback(...args);
        } else {
            trackEventsQueue.push({
                callback: callback as QueuedCallback,
                args,
            });
        }
    }

    protected trackEventInternal(trackModel: TrackModel): void {
        this.withTrackEventsQueue(super.trackEventInternal.bind(this), trackModel);
    }

    protected dispatchTrackEventInternal(
        event: TrackingEvent,
        action: string,
        eventName: string,
        customAttributes: Record<CustomAttribute, Primitive>,
    ): void {
        this.withTrackEventsQueue(
            super.dispatchTrackEventInternal.bind(this),
            event,
            action,
            eventName,
            customAttributes,
        );
    }
    protected trackPageViewInternal(customDimsData: ICustomDimensionData): void {
        this.withTrackEventsQueue(super.trackPageViewInternal.bind(this), customDimsData);
    }

    protected isTrackerReady(mixpanel: unknown): boolean {
        return !!mixpanel && "get_property" in (mixpanel as object) && isReadyAndIdentified;
    }

    private resetProperties(mixpanel: unknown): void {
        if (!this.isTrackerReady(mixpanel)) {
            return;
        }

        propertiesToResetOnPageView.forEach((prop) => {
            (mixpanel as { unregister: (prop: string) => void }).unregister(prop);
        });
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        super.resetProperties(mixpanel);
    }
}
